import { useMemo } from 'react'
import ModalProvider from 'mui-modal-provider'
import PropTypes from 'prop-types'
import { useLocalStorage } from 'usehooks-ts'

import CssBaseline from '@mui/material/CssBaseline'
import { createTheme, StyledEngineProvider, ThemeProvider as ThemeProviderMUI } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'

import AppVersionWatcher from '@shared/components/src/AppVersionWatcher'

import Toaster from '@components/Toast'

import componentsOverride from './overrides'
import CustomShadows from './shadows'
import DarkTheme from './theme/darkTheme'
import LightTheme from './theme/lightTheme'
import Typography from './typography'

export default function ThemeProvider({ children }) {
  const [mode] = useLocalStorage('color-scheme', 'light')

  const theme = useMemo(() => createTheme(mode === 'light' ? LightTheme : DarkTheme), [mode])
  const themeCustomShadows = useMemo(() => CustomShadows(theme), [theme])

  const themeOptions = useMemo(
    () => ({
      colorSchemes: {
        dark: true,
      },
      shape: {
        borderRadius: 8,
      },
      breakpoints: {
        values: {
          xs: 0,
          sm: 768,
          md: 1024,
          lg: 1266,
          xl: 1536,
          xxl: 1920,
        },
      },
      mixins: {
        sidebar: {
          fullWidth: 260,
          miniWidth: 64,
        },
        toolbar: {
          height: 54,
          minHeight: 54,
          paddingTop: 4,
          paddingBottom: 4,
        },
        breadcrumbs: {
          minHeight: 100,
        },
      },
      palette: theme.palette,
      customShadows: themeCustomShadows,
      typography: Typography,
      twilio: {
        videoWidth: 360,
        headerHeight: 42,
        footerHeight: 72,
        sidebarWidth: 300,
        sidebarMobileHeight: 90,
        sidebarMobilePadding: 8,
        participantBorderWidth: 2,
      },
    }),
    [theme, themeCustomShadows]
  )

  const themeOverride = createTheme(themeOptions)
  themeOverride.components = componentsOverride(themeOverride)

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <StyledEngineProvider injectFirst>
        <ThemeProviderMUI theme={themeOverride} defaultMode="light">
          <CssBaseline />
          <Toaster />
          <AppVersionWatcher />
          <ModalProvider>{children}</ModalProvider>
        </ThemeProviderMUI>
      </StyledEngineProvider>
    </LocalizationProvider>
  )
}

ThemeProvider.propTypes = {
  children: PropTypes.node,
}
